import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import Input, { Select } from '../../components/ui/Input';
import toast from 'react-hot-toast';

export default function AdminProducts() {
  const [productLines, setProductLines] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', fullName: '', organizationId: '' });
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [plRes, orgRes] = await Promise.all([
        api.get('/admin/product-lines'),
        api.get('/admin/organizations'),
      ]);
      setProductLines(plRes.data.productLines);
      setOrgs(orgRes.data.organizations);
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/admin/product-lines', {
        ...form,
        organizationId: parseInt(form.organizationId, 10),
      });
      toast.success('Product line created');
      setShowCreate(false);
      setForm({ name: '', fullName: '', organizationId: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally {
      setCreating(false);
    }
  };

  // Stats
  const totalProductLines = productLines.length;
  const totalAppliances = productLines.reduce((s, pl) => s + (pl._count?.appliances || 0), 0);
  const totalLicenses = productLines.reduce((s, pl) => s + (pl._count?.licenses || 0), 0);

  // Color class for product card top border
  const getCardColorClass = (name) => {
    const n = name.toUpperCase();
    if (n.includes('RISET')) return 'border-t-[#818cf8]';
    if (n.includes('PENT')) return 'border-t-[#fb7185]';
    if (n.includes('BENCH')) return 'border-t-[#f59e0b]';
    return 'border-t-[#06b6d4]';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-0">
        <div className="text-[11px] text-[#475569] mb-[2px]">Admin</div>
        <h2 className="text-[22px] font-bold text-[#e2e8f0] tracking-[-0.3px]">Products</h2>
        <p className="text-[13px] text-[#64748b] mt-[2px]">Manage product lines, appliances, and licenses</p>
        <div className="flex gap-2 mt-3">
          <Button onClick={() => setShowCreate(true)}>
            <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Product Line
          </Button>
          <Button variant="ghost" onClick={() => navigate('/admin/import')}>
            <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            Import Excel
          </Button>
        </div>
      </div>

      <div className="mt-5">
        {/* Stats row */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          <div className="bg-[#0f1623] border border-[rgba(148,163,184,0.06)] rounded-[10px] p-4 relative overflow-hidden border-t-2 border-t-[#06b6d4]">
            <div className="text-[11px] text-[#475569] font-medium mb-1.5">Product Lines</div>
            <div className="text-[28px] font-bold font-mono tracking-[-1px] text-[#06b6d4]">{totalProductLines}</div>
          </div>
          <div className="bg-[#0f1623] border border-[rgba(148,163,184,0.06)] rounded-[10px] p-4 relative overflow-hidden border-t-2 border-t-[#10b981]">
            <div className="text-[11px] text-[#475569] font-medium mb-1.5">Total AIBOX</div>
            <div className="text-[28px] font-bold font-mono tracking-[-1px] text-[#10b981]">{totalAppliances}</div>
            <div className="text-[10.5px] text-[#475569] mt-1">All registered</div>
          </div>
          <div className="bg-[#0f1623] border border-[rgba(148,163,184,0.06)] rounded-[10px] p-4 relative overflow-hidden border-t-2 border-t-[#f59e0b]">
            <div className="text-[11px] text-[#475569] font-medium mb-1.5">Total Licenses</div>
            <div className="text-[28px] font-bold font-mono tracking-[-1px] text-[#f59e0b]">{totalLicenses}</div>
            <div className="text-[10.5px] text-[#475569] mt-1">All pending</div>
          </div>
          <div className="bg-[#0f1623] border border-[rgba(148,163,184,0.06)] rounded-[10px] p-4 relative overflow-hidden border-t-2 border-t-[#ef4444]">
            <div className="text-[11px] text-[#475569] font-medium mb-1.5">Activated</div>
            <div className="text-[28px] font-bold font-mono tracking-[-1px] text-[#ef4444]">0</div>
            <div className="text-[10.5px] text-[#475569] mt-1">Awaiting delivery</div>
          </div>
        </div>

        {/* Product cards grid */}
        {productLines.length === 0 ? (
          <div className="bg-[#0f1623] border border-[rgba(148,163,184,0.06)] rounded-[10px] text-center py-12">
            <p className="text-[#64748b]">No product lines yet. Create one to get started.</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-[14px]">
            {productLines.map(pl => (
              <div
                key={pl.id}
                onClick={() => navigate(`/admin/products/${pl.id}`)}
                className={`bg-[#0f1623] border border-[rgba(148,163,184,0.06)] rounded-[10px] p-5 cursor-pointer transition-all hover:border-[rgba(148,163,184,0.10)] hover:bg-[#151d2e] hover:-translate-y-px relative overflow-hidden border-t-[2.5px] ${getCardColorClass(pl.name)}`}
              >
                <h3 className="text-[16px] font-bold text-[#e2e8f0] mb-[2px]">{pl.name}</h3>
                <div className="text-[12px] text-[#475569] mb-3">{pl.fullName}</div>
                <div className="text-[11px] text-[#64748b] mb-3 flex items-center gap-1">
                  <svg className="w-[13px] h-[13px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/></svg>
                  {pl.organization?.shortName || pl.organization?.name}
                </div>
                <div className="flex gap-2">
                  <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-[10px] py-[3px] rounded-[5px] bg-[rgba(16,185,129,0.08)] text-[#10b981] border border-[rgba(16,185,129,0.15)]">
                    {pl._count?.appliances || 0} AIBOX
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-[10px] py-[3px] rounded-[5px] bg-[rgba(6,182,212,0.06)] text-[#06b6d4] border border-[rgba(6,182,212,0.15)]">
                    {pl._count?.licenses || 0} licenses
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="New Product Line">
        <form onSubmit={handleCreate} className="space-y-4">
          <Select
            label="Organization"
            value={form.organizationId}
            onChange={(e) => setForm({ ...form, organizationId: e.target.value })}
            required
          >
            <option value="">Select organization...</option>
            {orgs.map(o => (
              <option key={o.id} value={o.id}>{o.shortName || o.name}</option>
            ))}
          </Select>
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            placeholder="e.g., RISET"
          />
          <Input
            label="Full Name"
            value={form.fullName}
            onChange={(e) => setForm({ ...form, fullName: e.target.value })}
            required
            placeholder="e.g., Laboratorium Riset Mobile"
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" type="button" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button type="submit" disabled={creating}>{creating ? 'Creating...' : 'Create'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
